"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Control = void 0;
var _react = _interopRequireWildcard(require("react"));
require("./Control.css");
var _useMergedRef = require("@cbhq/cds-common/hooks/useMergedRef");
var _usePrefixedId2 = require("@cbhq/cds-common/hooks/usePrefixedId");
var _zIndex = require("@cbhq/cds-common/tokens/zIndex");
var _cdsUtils = require("@cbhq/cds-utils");
var _layout = require("../layout");
var _Interactable = require("../system/Interactable");
var _TextBody = require("../typography/TextBody");
var _isRtl = require("../utils/isRtl");
var _linaria = require("../utils/linaria");
const _excluded = ["type", "checked", "disabled", "readOnly", "required", "value", "children", "label", "aria-labelledby", "background", "borderColor", "borderRadius", "borderWidth", "testID"];
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
const pointer = "cds-pointer-p2t57kg";
const controlInput = "cds-controlInput-cxj9qyy";
const interactableContainer = "cds-interactableContainer-i3ulf59";
const ControlWithRef = /*#__PURE__*/(0, _react.forwardRef)(function ControlWithRef(_ref, ref) {
  let type = _ref.type,
    _ref$checked = _ref.checked,
    checked = _ref$checked === void 0 ? false : _ref$checked,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    _ref$required = _ref.required,
    required = _ref$required === void 0 ? false : _ref$required,
    value = _ref.value,
    children = _ref.children,
    label = _ref.label,
    ariaLabelledby = _ref['aria-labelledby'],
    background = _ref.background,
    borderColor = _ref.borderColor,
    borderRadius = _ref.borderRadius,
    borderWidth = _ref.borderWidth,
    testID = _ref.testID,
    htmlProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  if ((0, _cdsUtils.isDevelopment)() && !children && !ariaLabelledby) {
    // eslint-disable-next-line no-console
    console.warn(`Please provide an aria label for the control component ${value} either through the children or aria-labelledby prop.`);
  }
  // Setup a11y IDs
  const _usePrefixedId = (0, _usePrefixedId2.usePrefixedId)(['trigger', 'collapsible']),
    id1 = _usePrefixedId[0],
    id2 = _usePrefixedId[1];
  const labelId = (0, _react.useMemo)(() => ariaLabelledby !== null && ariaLabelledby !== void 0 ? ariaLabelledby : id1, [ariaLabelledby, id1]);
  const inputId = (0, _react.useMemo)(() => {
    var _htmlProps$id;
    return (_htmlProps$id = htmlProps.id) !== null && _htmlProps$id !== void 0 ? _htmlProps$id : id2;
  }, [htmlProps.id, id2]);
  const internalInputRef = (0, _react.useRef)();
  const inputRef = (0, _useMergedRef.useMergedRef)(ref, internalInputRef);
  const inputProps = (0, _react.useMemo)(() => Object.assign({
    ref: inputRef,
    type,
    checked,
    disabled,
    readOnly,
    required,
    value,
    id: inputId,
    'aria-labelledby': labelId,
    'aria-checked': checked,
    'aria-required': type !== 'checkbox' ? required : undefined,
    'data-testid': testID
  }, htmlProps), [checked, disabled, htmlProps, inputId, inputRef, labelId, readOnly, required, testID, type, value]);
  const iconNode = (0, _react.useMemo)(() => /*#__PURE__*/_react.default.createElement(_Interactable.Interactable, {
    transparentWhileInactive: true,
    as: "div",
    background: background !== null && background !== void 0 ? background : checked ? 'primary' : 'background',
    borderColor: borderColor,
    borderRadius: borderRadius,
    borderWidth: borderWidth,
    className: interactableContainer,
    disabled: disabled || readOnly,
    testID: testID ? `${testID}-parent` : undefined
  }, /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("input", _extends({
    className: (0, _linaria.cx)(controlInput, pointer)
  }, inputProps)), children)), [background, borderColor, borderRadius, borderWidth, checked, children, disabled, inputProps, readOnly, testID]);
  const InternalLabel = (0, _react.useMemo)(() => /*#__PURE__*/_react.default.createElement("label", {
    className: pointer,
    htmlFor: inputId
  }, /*#__PURE__*/_react.default.createElement(_layout.Box, {
    alignItems: "flex-start",
    flexDirection: (0, _isRtl.isRtl)() ? 'row-reverse' : 'row'
  }, /*#__PURE__*/_react.default.createElement(_layout.Box, {
    alignItems: "center",
    height: "var(--body-line-height)",
    role: "presentation"
  }, iconNode), /*#__PURE__*/_react.default.createElement(_layout.Spacer, {
    horizontal: 1
  }), /*#__PURE__*/_react.default.createElement(_TextBody.TextBody, {
    as: "span",
    color: checked ? 'foreground' : 'foregroundMuted',
    disabled: disabled || readOnly,
    id: labelId
  }, label))), [checked, disabled, iconNode, inputId, label, labelId, readOnly]);

  // If no label (children) is provided, consumer should wrap the checkbox with <label> or provide a value for the aria-labelledby prop.
  return label ? InternalLabel : iconNode;
});
const Control = /*#__PURE__*/(0, _react.memo)(ControlWithRef);
exports.Control = Control;